<template>
  <v-card class="w-[43rem]" flat>
    <div class="flex justify-between p-4 bg-gray-200 dark-bg-custom">
      <h4 class="text-xl">{{ editCase ? "Edit Case" : "Add Case" }}</h4>
      <v-icon @click="cancel">mdi-close</v-icon>
    </div>
    <div class="p-4">
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-text-field
          v-model="caseNo"
          :label="configLang.caseNo?.displayName"
          :rules="[
            (v) => !!v || `${configLang.caseNo?.displayName} is required`,
          ]"
          v-show="configLang.caseNo?.visibility"
          outlined
          dense
        />
        <v-text-field
          v-model="typeOfLoan"
          :label="configLang.typeOfLoan?.displayName || 'Type of loan'"
          v-show="configLang.typeOfLoan?.visibility"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          v-model="bkt"
          :label="configLang.bkt?.displayName || 'Bucket'"
          v-show="configLang.bkt?.visibility"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          v-model="pos"
          :label="configLang.pos?.displayName || 'POS'"
          v-show="configLang.pos?.visibility"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          v-model="emiAmount"
          :label="configLang.emiAmount?.displayName || 'EMI Amount'"
          v-show="configLang.emiAmount?.visibility"
          outlined
          dense
        ></v-text-field>
        <v-text-field
          v-model="tenure"
          :label="configLang.tenure?.displayName || 'Tenure'"
          v-show="configLang.tenure?.visibility"
          outlined
          dense
        ></v-text-field>
        <v-autocomplete
          v-model="customerId"
          :label="`Search Customer Name`"
          :items="customers"
          :item-text="getFullName"
          :search-input.sync="searchCustomer"
          item-value="_id"
          @update:search-input="debounceCustomer"
          hide-no-data
          outlined
          dense
        >
        </v-autocomplete>
        <v-autocomplete
          v-model="assignedTo"
          :label="`Search ${
            configLang.assignedTo.displayName || 'Assigned To'
          }`"
          :items="employees"
          :item-text="getEmpFullName"
          :search-input.sync="searchUser"
          item-value="_id"
          @update:search-input="debounceUser"
          hide-no-data
          v-show="configLang.assignedTo?.visibility"
          hide-details
          outlined
          dense
        >
        </v-autocomplete>
        <div class="flex items-center justify-between mt-4">
          <v-btn outlined class="w-72 h-14 mr-8 rounded-md" @click="cancel">
            Cancel
          </v-btn>
          <v-btn
            class="white--text w-72 h-14 rounded-md"
            :color="$vuetify.theme.currentTheme.primary"
            @click="addCase"
          >
            {{ editCase ? "Update" : "Add" }}
          </v-btn>
        </div>
      </v-form>
    </div>
  </v-card>
</template>
<script>
import { fullName, debounce } from "@/utils/common";
export default {
  name: "AddCases",
  data() {
    return {
      userData: this.$storage.getUniversal("user"),
      caseType: this.caseConfig.name,
      caseNo: "",
      typeOfLoan: "",
      bkt: "",
      pos: null,
      emiAmount: null,
      customerId: null,
      searchCustomer: "",
      tenure: null,
      assignedTo: null,
      valid: true,
      customers: [],
      employees: [],
      searchUser: "",
      loanTypes: "",
    };
  },
  props: {
    editCase: Object,
    caseConfig: Object,
  },
  watch: {
    editCase: {
      handler(newVal) {
        this.editCase = newVal;
        this.populate();
      },
      deep: true,
    },
  },
  computed: {
    configLang() {
      return this.$props.caseConfig.fieldMappings;
    },
  },
  methods: {
    async addCase() {
      try {
        const isValid = this.$refs.form.validate();
        if (!isValid) {
          this.$toast.error("Please fill the required fields");
          return;
        }
        const payload = {
          caseType: this.caseType,
          caseNo: this.caseNo,
          typeOfLoan: this.typeOfLoan || undefined,
          bkt: this.bkt || undefined,
          pos: this.pos || undefined,
          emiAmount: this.emiAmount || undefined,
          tenure: this.tenure || undefined,
          customer: this.customerId,
          assignedTo: this.assignedTo,
        };
        if (this.$props.caseConfig?.branch) {
          payload.branch = this.$props.caseConfig?.branch;
        }

        let url = "/workforce/collection/case";
        let method = "post";

        if (this.editCase) {
          method = "put";
          url += `/${this.editCase._id}`;
        }

        await this.$axios[method](url, payload);

        this.cancel();
      } catch (error) {
        this.$toast.error(error.response?.data?.message);
        console.error(error);
      }
    },
    debounceUser: debounce(async function (text) {
      if (!text || this.assignedTo) return true;
      this.fetchUsers();
    }, 1000),
    async fetchUsers() {
      try {
        const params = {};
        if (this.searchUser) {
          params.search = this.searchUser;
        }
        if (
          this.userData?.branch &&
          !this.userData?.role?.includes("WFM_ADMIN")
        ) {
          params.branch = this.userData?.branch;
        }
        const response = await this.$axios.get("/workforce/v2/users", {
          params,
        });
        this.employees = response.data?.users;
      } catch (error) {
        console.log(error);
      }
    },
    debounceCustomer: debounce(async function (text) {
      if (!text || this.customerId) return true;
      this.fetchCustomers();
    }, 800),
    async fetchCustomers() {
      try {
        const params = {};
        if (this.searchCustomer) {
          params.search = this.searchCustomer;
        }
        const response = await this.$axios.get("/workforce/customers", {
          params,
        });
        this.customers = response.data.customers;
      } catch (error) {
        console.log(error);
        const errorMessage = error?.response?.data?.message || error;
        this.$toast.error(errorMessage);
      }
    },
    getFullName(item) {
      return item.customerFullName;
    },
    getEmpFullName(item) {
      return fullName(item);
    },
    cancel() {
      this.resetForm();
      this.$refs.form.reset();
      this.$emit("caseAdded");
    },
    resetForm() {
      this.caseType = this.caseConfig.name;
      this.caseNo = "";
      this.typeOfLoan = "";
      this.bkt = "";
      this.pos = null;
      this.emiAmount = null;
      this.tenure = null;
    },
    populate() {
      if (this.editCase) {
        this.caseType = this.editCase?.caseType;
        this.caseNo = this.editCase?.caseNo;
        this.typeOfLoan = this.editCase?.typeOfLoan;
        this.bkt = this.editCase?.bkt;
        this.pos = this.editCase?.pos;
        this.emiAmount = this.editCase?.emiAmount;
        this.tenure = this.editCase?.tenure;
        this.assignedTo = this.editCase?.assignedTo?._id;
        this.customerId = this.editCase?.customer?._id;
        if (this.editCase?.customer?.customerFullName) {
          this.searchCustomer = this.editCase?.customer?.customerFullName;
          this.fetchCustomers();
        }
        if (this.editCase?.assignedTo?.firstName) {
          this.searchUser = this.editCase?.assignedTo?.firstName;
          this.fetchUsers();
        }
      }
    },
  },
  async mounted() {
    this.populate();
  },
};
</script>
<style></style>
